import {
  EditableRing,
  GeofenceArea,
  GeofenceGeometry,
  GeoJsonLinearRing,
  GeoJsonPolygonCoordinates,
  GeoJsonPosition,
  LatLngPoint,
} from './geofence.model';

/**
 * Pure geometry helpers for the geofence editor.
 *
 * Nothing here touches `google.maps`, on purpose: the conversions and the
 * sanity checks are the parts most likely to be wrong, and they must be
 * testable without loading the Maps SDK.
 *
 * The server is the authority on validity — everything `validateAreas` returns
 * at `warning` severity is a UX hint only. Only `error` severity blocks a save,
 * and it is reserved for geometry that cannot be serialised to legal GeoJSON.
 */

/**
 * Caps mirrored from `geofenceAdminService` so the admin hears about it before a
 * rejected save. The vertex and byte caps are the server's actual limits; the
 * area caps are UX judgement only and the server has no opinion on them.
 */
export const GEOFENCE_VERTEX_HINT_CAP = 2000;
export const GEOFENCE_GEOMETRY_BYTE_CAP = 256 * 1024;
export const GEOFENCE_AREA_HINT_CAP_SQ_KM = 5000;
export const GEOFENCE_MIN_AREA_HINT_SQ_M = 100;

const EARTH_RADIUS_METERS = 6378137;

// ---------------------------------------------------------------------------
// Conversion: editor (lat/lng, open rings) <-> GeoJSON ([lng, lat], closed rings)
// ---------------------------------------------------------------------------

/** Returns the ring wound the requested way, reversing a copy only if needed. */
export function orientRing(points: EditableRing, counterClockwise: boolean): EditableRing {
  return ringIsCounterClockwise(points) === counterClockwise ? points : [...points].reverse();
}

function closeRing(points: EditableRing, counterClockwise: boolean): GeoJsonLinearRing {
  const ring: GeoJsonLinearRing = orientRing(points, counterClockwise).map(p => [p.lng, p.lat] as GeoJsonPosition);
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (!first || !last) return ring;
  if (first[0] !== last[0] || first[1] !== last[1]) {
    ring.push([first[0], first[1]]);
  }
  return ring;
}

function areaToPolygonCoordinates(area: GeofenceArea): GeoJsonPolygonCoordinates {
  // RFC 7946: exterior ring counter-clockwise, interior rings (holes) clockwise.
  return [closeRing(area.outer, true), ...area.holes.map(h => closeRing(h, false))];
}

/**
 * Serialise the editor's areas. One area yields a `Polygon`, several a
 * `MultiPolygon`. Returns null when there is nothing drawable yet.
 */
export function areasToGeometry(areas: GeofenceArea[]): GeofenceGeometry | null {
  const usable = areas.filter(a => a.outer.length >= 3);
  if (!usable.length) return null;
  if (usable.length === 1) {
    return { type: 'Polygon', coordinates: areaToPolygonCoordinates(usable[0]) };
  }
  return { type: 'MultiPolygon', coordinates: usable.map(areaToPolygonCoordinates) };
}

function openRing(ring: GeoJsonLinearRing): EditableRing {
  const points: EditableRing = ring.map(([lng, lat]) => ({ lat, lng }));
  const first = points[0];
  const last = points[points.length - 1];
  if (points.length > 1 && first && last && first.lat === last.lat && first.lng === last.lng) {
    points.pop();
  }
  return points;
}

let areaIdCounter = 0;
export function nextAreaId(): string {
  areaIdCounter += 1;
  return `area-${areaIdCounter}`;
}

/** Parse stored geometry back into editable areas. Unknown shapes yield `[]`. */
export function geometryToAreas(geometry: GeofenceGeometry | null | undefined): GeofenceArea[] {
  if (!geometry) return [];
  const polygons: GeoJsonPolygonCoordinates[] =
    geometry.type === 'Polygon'
      ? [geometry.coordinates]
      : geometry.type === 'MultiPolygon'
        ? geometry.coordinates
        : [];

  return polygons
    .filter(rings => Array.isArray(rings) && rings.length > 0)
    .map(rings => ({
      id: nextAreaId(),
      outer: openRing(rings[0]),
      holes: rings.slice(1).map(openRing),
    }))
    .filter(area => area.outer.length >= 3);
}

// ---------------------------------------------------------------------------
// Ring maths
// ---------------------------------------------------------------------------

/** Planar shoelace signed area. Sign only — magnitude is meaningless in degrees. */
export function ringSignedArea(points: EditableRing): number {
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    sum += a.lng * b.lat - b.lng * a.lat;
  }
  return sum / 2;
}

export function ringIsCounterClockwise(points: EditableRing): boolean {
  return ringSignedArea(points) > 0;
}

/**
 * Spherical excess area in square metres. Matches
 * `google.maps.geometry.spherical.computeArea` closely enough for a size hint,
 * without needing the SDK loaded.
 */
export function ringAreaSqMeters(points: EditableRing): number {
  if (points.length < 3) return 0;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  let total = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    total += toRad(b.lng - a.lng) * (2 + Math.sin(toRad(a.lat)) + Math.sin(toRad(b.lat)));
  }
  return Math.abs((total * EARTH_RADIUS_METERS * EARTH_RADIUS_METERS) / 2);
}

/** Outer area minus its holes, in square metres. */
export function areaSqMeters(area: GeofenceArea): number {
  const holes = area.holes.reduce((sum, h) => sum + ringAreaSqMeters(h), 0);
  return Math.max(0, ringAreaSqMeters(area.outer) - holes);
}

export function totalAreaSqMeters(areas: GeofenceArea[]): number {
  return areas.reduce((sum, a) => sum + areaSqMeters(a), 0);
}

export function countVertices(areas: GeofenceArea[]): number {
  return areas.reduce((sum, a) => sum + a.outer.length + a.holes.reduce((h, r) => h + r.length, 0), 0);
}

/** Ray-casting containment test. Points exactly on an edge are unspecified. */
export function pointInRing(point: LatLngPoint, ring: EditableRing): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i];
    const b = ring[j];
    const straddles = a.lat > point.lat !== b.lat > point.lat;
    if (!straddles) continue;
    const x = ((b.lng - a.lng) * (point.lat - a.lat)) / (b.lat - a.lat) + a.lng;
    if (point.lng < x) inside = !inside;
  }
  return inside;
}

function orientation(p: LatLngPoint, q: LatLngPoint, r: LatLngPoint): number {
  const v = (q.lat - p.lat) * (r.lng - q.lng) - (q.lng - p.lng) * (r.lat - q.lat);
  if (Math.abs(v) < 1e-12) return 0;
  return v > 0 ? 1 : 2;
}

function onSegment(p: LatLngPoint, q: LatLngPoint, r: LatLngPoint): boolean {
  return (
    q.lng <= Math.max(p.lng, r.lng) &&
    q.lng >= Math.min(p.lng, r.lng) &&
    q.lat <= Math.max(p.lat, r.lat) &&
    q.lat >= Math.min(p.lat, r.lat)
  );
}

function segmentsIntersect(p1: LatLngPoint, q1: LatLngPoint, p2: LatLngPoint, q2: LatLngPoint): boolean {
  const o1 = orientation(p1, q1, p2);
  const o2 = orientation(p1, q1, q2);
  const o3 = orientation(p2, q2, p1);
  const o4 = orientation(p2, q2, q1);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(p1, p2, q1)) return true;
  if (o2 === 0 && onSegment(p1, q2, q1)) return true;
  if (o3 === 0 && onSegment(p2, p1, q2)) return true;
  if (o4 === 0 && onSegment(p2, q1, q2)) return true;
  return false;
}

/**
 * True when a ring crosses itself. O(n^2), which is fine at the vertex cap and
 * is only ever run on the vertices an admin drew by hand.
 */
export function ringSelfIntersects(points: EditableRing): boolean {
  const n = points.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    const a1 = points[i];
    const a2 = points[(i + 1) % n];
    for (let j = i + 1; j < n; j++) {
      // Skip adjacent segments (and the wrap-around pair) — they share a vertex.
      if (j === i || (j + 1) % n === i || (i + 1) % n === j) continue;
      const b1 = points[j];
      const b2 = points[(j + 1) % n];
      if (segmentsIntersect(a1, a2, b1, b2)) return true;
    }
  }
  return false;
}

/** True when two rings cross edges or one fully contains the other. */
export function ringsOverlap(a: EditableRing, b: EditableRing): boolean {
  for (let i = 0; i < a.length; i++) {
    const a1 = a[i];
    const a2 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j++) {
      if (segmentsIntersect(a1, a2, b[j], b[(j + 1) % b.length])) return true;
    }
  }
  return (a.length > 0 && pointInRing(a[0], b)) || (b.length > 0 && pointInRing(b[0], a));
}

export function ringCentroid(points: EditableRing): LatLngPoint | null {
  if (!points.length) return null;
  const sum = points.reduce((acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }), { lat: 0, lng: 0 });
  return { lat: sum.lat / points.length, lng: sum.lng / points.length };
}

export interface GeofenceBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export function boundsOfAreas(areas: GeofenceArea[]): GeofenceBounds | null {
  const points = areas.flatMap(a => a.outer);
  if (!points.length) return null;
  return points.reduce<GeofenceBounds>(
    (acc, p) => ({
      north: Math.max(acc.north, p.lat),
      south: Math.min(acc.south, p.lat),
      east: Math.max(acc.east, p.lng),
      west: Math.min(acc.west, p.lng),
    }),
    { north: -90, south: 90, east: -180, west: 180 },
  );
}

// ---------------------------------------------------------------------------
// Sanity checks
// ---------------------------------------------------------------------------

export type GeofenceIssueSeverity = 'error' | 'warning';

export interface GeofenceIssue {
  severity: GeofenceIssueSeverity;
  message: string;
  /** Index into the areas array, when the issue is about one area. */
  areaIndex?: number;
}

/**
 * Client-side sanity pass. Errors mean "cannot be saved as valid GeoJSON";
 * warnings mean "this looks wrong, but the server decides".
 */
export function validateAreas(areas: GeofenceArea[]): GeofenceIssue[] {
  const issues: GeofenceIssue[] = [];

  if (!areas.length) {
    issues.push({ severity: 'error', message: 'Draw at least one area before saving.' });
    return issues;
  }

  areas.forEach((area, index) => {
    const label = areas.length > 1 ? `Area ${index + 1}` : 'This area';

    if (area.outer.length < 3) {
      issues.push({ severity: 'error', areaIndex: index, message: `${label} needs at least 3 points.` });
      return;
    }

    if (ringSelfIntersects(area.outer)) {
      issues.push({
        severity: 'warning',
        areaIndex: index,
        message: `${label} crosses over itself. Untangle the boundary or the server may reject it.`,
      });
    }

    const sqM = areaSqMeters(area);
    if (sqM < GEOFENCE_MIN_AREA_HINT_SQ_M) {
      issues.push({
        severity: 'warning',
        areaIndex: index,
        message: `${label} is smaller than a GPS fix is accurate to. Members may be denied even when standing inside it.`,
      });
    }

    area.holes.forEach((hole, holeIndex) => {
      const holeLabel = `Excluded area ${holeIndex + 1} in ${label.toLowerCase()}`;
      if (hole.length < 3) {
        issues.push({ severity: 'error', areaIndex: index, message: `${holeLabel} needs at least 3 points.` });
        return;
      }
      if (!hole.every(p => pointInRing(p, area.outer))) {
        issues.push({
          severity: 'warning',
          areaIndex: index,
          message: `${holeLabel} sticks out past the boundary. Only the part inside will be excluded.`,
        });
      }
      if (ringSelfIntersects(hole)) {
        issues.push({ severity: 'warning', areaIndex: index, message: `${holeLabel} crosses over itself.` });
      }
    });
  });

  for (let i = 0; i < areas.length; i++) {
    for (let j = i + 1; j < areas.length; j++) {
      if (areas[i].outer.length >= 3 && areas[j].outer.length >= 3 && ringsOverlap(areas[i].outer, areas[j].outer)) {
        issues.push({
          severity: 'warning',
          message: `Area ${i + 1} and area ${j + 1} overlap. That is allowed, but they can be drawn as one area instead.`,
        });
      }
    }
  }

  const vertices = countVertices(areas);
  if (vertices > GEOFENCE_VERTEX_HINT_CAP) {
    issues.push({
      severity: 'warning',
      message: `${vertices} points across all areas — over the ${GEOFENCE_VERTEX_HINT_CAP}-point limit the server enforces. Simplify the boundary.`,
    });
  }

  const geometryBytes = estimateGeometryBytes(areas);
  if (geometryBytes > GEOFENCE_GEOMETRY_BYTE_CAP) {
    issues.push({
      severity: 'warning',
      message: `The shape is about ${Math.round(geometryBytes / 1024)} KB — over the ${Math.round(
        GEOFENCE_GEOMETRY_BYTE_CAP / 1024,
      )} KB limit the server enforces. Simplify the boundary.`,
    });
  }

  const totalSqKm = totalAreaSqMeters(areas) / 1_000_000;
  if (totalSqKm > GEOFENCE_AREA_HINT_CAP_SQ_KM) {
    issues.push({
      severity: 'warning',
      message: `${formatArea(totalAreaSqMeters(areas))} total — over the ${GEOFENCE_AREA_HINT_CAP_SQ_KM.toLocaleString()} km² limit the server enforces.`,
    });
  }

  return issues;
}

/**
 * Rough serialised size, without building the JSON. Each position costs two
 * numbers plus punctuation; ~24 bytes is a fair average at GPS precision.
 */
export function estimateGeometryBytes(areas: GeofenceArea[]): number {
  return countVertices(areas) * 24 + areas.length * 32 + 32;
}

export function hasBlockingIssue(issues: GeofenceIssue[]): boolean {
  return issues.some(i => i.severity === 'error');
}

export function formatArea(sqMeters: number): string {
  if (sqMeters < 1_000_000) return `${Math.round(sqMeters).toLocaleString()} m²`;
  const sqKm = sqMeters / 1_000_000;
  return `${sqKm < 10 ? sqKm.toFixed(2) : Math.round(sqKm).toLocaleString()} km²`;
}
